import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import CapSvg from '../../../assets/icons/CapSvg';
import ClockSvg from '../../../assets/icons/ClockSvg';
import theme from '../../../assets/theme';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import { useQueryGenerators } from '../../../hooks/useQueryGenerators';
import { getGeneratorsUrl } from '../../../services/DiscoverService/discoverUtils';
import { GeneratorImage } from '../../_molecules/GeneratorCard';
import { SubPageLayout } from './CreatePage';
import { CreateWithAIFormState } from './types';

export const ResponsiveGeneratorsGridContainer = styled.div<{ $isCentered?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;

  @media (max-width: 4096px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1440px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 800px) {
    // 800 we go into mobile screen mode
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

const DescriptionSection = styled.div`
  display: flex;
  width: 100%;
  justify-content: start;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: ${theme.colors.text.darkGrey};
  margin-bottom: 0px;
  flex-grow: 1;

  span {
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 0;
  }
`;

const cardVariants = (hoveredIndex: number | null) => ({
  initial: {
    scale: 1,
    opacity: 1,
    zIndex: 5,
    transition: { duration: 0.3 },
  },
  hover: (index: number) => ({
    scale: hoveredIndex === index ? 1.025 : 1,
    zIndex: hoveredIndex === index ? 10 : 5,
    boxShadow:
      hoveredIndex === index
        ? theme.effects.magicFloatingShadowHovered
        : theme.effects.magicFloatingShadow,
    transition: { duration: 0.3, type: 'spring', bounce: 0.5, stiffness: 300 },
  }),
});

const FloatingCardContainer = styled.div`
  width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  background-color: ${theme.colors.paleBeige};
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 100px;
  grid-template-columns: 1fr;
`;

const Card = styled(motion.div)`
  width: 100%;
  height: 100%;
  max-width: 650px;
  background-color: white;
  border-radius: 20px;
  position: relative;
`;

const ImageSection = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;

  & > div {
    width: 100%;
    height: 100%;
  }

  img {
    border-radius: 10px 10px 0px 0px;
  }
`;

const TextSection = styled.div`
  padding: 10px 5px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h3 {
    height: 50px;
    width: 100%;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 0px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2; //allows us to limit the number of lines displayed to 2 and add ellipsis for overflow
    text-overflow: ellipsis;
  }
`;

const groupedGrades = (grades: string[]) => {
  const formattedGrade = (grade: number) => {
    switch (grade) {
      case 0: // Kindergarten
        return 'K';
      case 1:
        return '1st';
      case 2:
        return '2nd';
      case 3:
        return '3rd';
      default:
        return grade + 'th';
    }
  };

  const sortedGrades = grades.sort((a, b) => parseInt(a) - parseInt(b));
  const groupedGrades = [];
  let start = 0;
  let end = 0;

  for (let i = 0; i <= sortedGrades.length; i++) {
    if (i === 0) {
      start = parseInt(sortedGrades[i]);
      end = start;
      continue;
    }
    if (parseInt(sortedGrades[i]) - end === 1) {
      end = parseInt(sortedGrades[i]);
    } else {
      if (start === end) {
        groupedGrades.push(formattedGrade(start));
      } else {
        groupedGrades.push(`${formattedGrade(start)}-${formattedGrade(end)}`);
      }
      start = parseInt(sortedGrades[i]);
      end = start;
    }
  }

  return (
    <>
      {groupedGrades.map(grade => (
        <span key={grade}>
          <CapSvg /> {grade}
        </span>
      ))}
    </>
  );
};

const CreateWithAIFloatingCard = ({
  img,
  title,
  grades,
  durationInMins,
  onClick,
}: {
  img: React.ReactNode;
  title: string;
  grades: string[];
  durationInMins?: number;
  onClick?: () => void;
}) => {
  return (
    <FloatingCardContainer onClick={onClick}>
      <ImageSection>{img}</ImageSection>
      <TextSection>
        <h3>{title}</h3>
        <DescriptionSection>
          {groupedGrades(grades)}
          {durationInMins && (
            <>
              <span>•</span>
              <span>
                <ClockSvg /> {durationInMins} minute
                {durationInMins === 1 ? '' : 's'}
              </span>
            </>
          )}
        </DescriptionSection>
      </TextSection>
    </FloatingCardContainer>
  );
};

export const FindCategorizedLessonsTab = ({
  lessonCategoryId,
  categoryTitle,
  selectedCategory,
}: {
  lessonCategoryId?: string;
  categoryTitle?: string;
  selectedCategory?: string;
}) => {
  const { userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null); // Track the hovered card
  const {
    generators,
    isFetchingGenerators,
    isFetchingNextPageGenerators,
    fetchNextPageGenerators,
    hasNextPageGenerators,
  } = useQueryGenerators({
    searchState: {
      categories:
        lessonCategoryId && selectedCategory ? [lessonCategoryId, selectedCategory] : [],
      grades: [],
      searchPhrase: undefined,
      subjects: [],
    },
  });

  const orderGenerators = useMemo(() => {
    if (lessonCategoryId === 'daily_routines') {
      //Place short 5min CLs at the top
      const fiveMinGenerators = generators.filter(
        generator => generator?.aiContent?.durationInMins === 5,
      );
      const otherGenerators = generators.filter(
        generator => generator?.aiContent?.durationInMins !== 5,
      );
      return [...fiveMinGenerators, ...otherGenerators];
    }

    return generators;
  }, [generators, lessonCategoryId]);

  const { ref: scrollRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPageGenerators && !isFetchingGenerators) {
      fetchNextPageGenerators();
    }
  }, [inView, hasNextPageGenerators, isFetchingGenerators, fetchNextPageGenerators]);
  return (
    <SubPageLayout pageTitle={categoryTitle ? categoryTitle : undefined}>
      <FormContainer>
        <>
          {isFetchingGenerators && orderGenerators.length === 0 && (
            <ResponsiveGeneratorsGridContainer
              style={{
                width: '100%',
                maxHeight: 180,
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 100, color: theme.colors.orange }}
                    spin
                  />
                }
              />
            </ResponsiveGeneratorsGridContainer>
          )}
          <ResponsiveGeneratorsGridContainer style={{ marginBottom: 30 }}>
            {orderGenerators.length > 0 &&
              orderGenerators.map((generator, i) => {
                return (
                  <Link
                    href={`${getGeneratorsUrl({
                      title: generator.title,
                      generatorId: generator.id,
                    })}?fromLessonCategory=${lessonCategoryId}&fromCategory=${selectedCategory}`}
                    key={generator.id}
                  >
                    <a style={{ height: '100%', width: '100%' }}>
                      <Card
                        key={generator.id}
                        variants={cardVariants(hoveredIndex)}
                        custom={i}
                        initial="initial"
                        animate="animate"
                        whileHover="hover" // Activate hover state
                        onMouseEnter={() => setHoveredIndex(i)} // Set the hovered card
                        onMouseLeave={() => setHoveredIndex(null)} // Reset on hover end
                      >
                        <CreateWithAIFloatingCard
                          key={generator.id}
                          img={
                            <GeneratorImage
                              coverImageId={generator.coverImageId}
                              status={generator.status}
                              firstPublished={generator.firstPublished}
                              isPremiumGenerator={
                                generator?.aiContent?.isPremiumGenerator
                              }
                              generatorPlan={generator?.aiContent?.generatorPlan}
                              userAccessBadge={userAccessBadge}
                              isStandardsAligned={!!generator.learningStandards}
                            />
                          }
                          title={generator.title || ''}
                          grades={generator.grades}
                          durationInMins={
                            generator?.aiContent?.durationInMins || undefined
                          }
                        />
                      </Card>
                    </a>
                  </Link>
                );
              })}
          </ResponsiveGeneratorsGridContainer>
          {isFetchingNextPageGenerators && (
            <ResponsiveGeneratorsGridContainer
              style={{
                width: '100%',
                maxHeight: 180,
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 100, color: theme.colors.orange }}
                    spin
                  />
                }
              />
            </ResponsiveGeneratorsGridContainer>
          )}
        </>
        <div style={{ width: '100%', height: 100 }} ref={scrollRef} />
      </FormContainer>
    </SubPageLayout>
  );
};

const FormContainer = styled.div`
  padding-top: 30px;
  margin: auto;
  width: 80%;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }
`;
